import React from "react";
import { ArrowRightIcon } from "../../../components/ui";
import type { TermChangeView } from "../termChange";

/**
 * What is being left and what is being bought, side by side, over the summary
 * that prices the move.
 *
 * A change of term is the one purchase on this screen that is about two terms
 * rather than one. The panel underneath can only talk about the one being
 * bought — that is what its figures are — so without this the handover below
 * it is the first mention anywhere that a term is being left at all, and
 * "Current plan until 20 Aug, 2027" is a line nobody can place.
 *
 * Purple rather than the brand teal, and the same purple the coupon tag and
 * the saving line already wear: everything on this checkout that is about
 * money coming off or a term changing hands is in that ink. The teal is what
 * the thing being bought is set in, and the two terms on this band are not
 * that.
 *
 * Named out loud for a reader who is not looking at it: a label, an arrow and
 * a label is a sentence to the eye and three fragments to a screen reader, so
 * the group carries the sentence and the pieces are hidden from it.
 */
const TermChangeBand: React.FC<{ change: TermChangeView }> = ({ change }) => (
  <div
    role="group"
    aria-label={`${change.queued ? "Downgrading" : "Upgrading"} from ${
      change.fromLabel
    } to ${change.toLabel}`}
    className="card-surface flex items-center gap-3 border border-[#D9CCFA] bg-[#F7F3FF] px-4 py-3 3xl:px-5 3xl:py-4"
  >
    <div className="min-w-0 flex-1" aria-hidden="true">
      <p className="text-[11px] 3xl:text-[12px] leading-none text-[#7A6AA8]">
        From
      </p>
      <p className="mt-1 truncate text-[14px] 3xl:text-[15px] font-semibold text-ink">
        {change.fromLabel}
      </p>
    </div>

    <ArrowRightIcon
      aria-hidden
      className="h-5 w-5 flex-shrink-0 text-[#6234CC]"
    />

    {/* Right-aligned, so the two terms sit against the ends of the band and
        the arrow has the room between them rather than beside them. */}
    <div className="min-w-0 flex-1 text-right" aria-hidden="true">
      <p className="text-[11px] 3xl:text-[12px] leading-none text-[#7A6AA8]">
        To
      </p>
      <p className="mt-1 truncate text-[14px] 3xl:text-[15px] font-semibold text-ink">
        {change.toLabel}
      </p>
    </div>
  </div>
);

export default TermChangeBand;
